import React, { useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import ProjectCell from '../ProjectCell';
import ColumnMenu from './ColumnMenu';
import ColumnMenuHost from './ColumnMenuHost';
import { NtIcon, Pill } from '../NotionUI';
import { useLanguage } from '../../i18n/LanguageContext';
import {
  TYPE_ICONS,
  MIN_WIDTH,
  orderedColumns,
  allColumnsInOrder,
  columnWidth,
  groupKeyToValue,
  flattenTree,
  descendantIds,
} from '../../utils/projectView';
import { optionColor } from '../ProjectCell';
import { parseClipboardTable } from '../../utils/csv';

const LEAD_WIDTH = 58; // casella di selezione + maniglia di trascinamento
const END_COLUMNS_WIDTH = 190; // colonna dei ticket + azioni

/**
 * Vista tabella: selezione e trascinamento delle righe, sotto-attività ad
 * albero, colonne ridimensionabili e riordinabili, navigazione con la
 * tastiera (frecce, Invio), incolla da Excel, menu per ogni colonna e per ogni
 * riga, raggruppamento.
 */
function ProjectTable({
  columns,
  tasks,
  allTasks,
  groups,
  groupColumn,
  treeMode,
  config,
  users,
  focusTaskId,
  selectedIds,
  canReorder,
  onFocusDone,
  onPatchTask,
  onTaskReplace,
  onError,
  onAddTask,
  onOpenTask,
  onToggleSelect,
  onToggleAll,
  onReorder,
  onRowAction,
  onPasteTable,
  onConfig,
  onColumnSubmit,
  onColumnAction,
}) {
  const { t } = useLanguage();
  const location = useLocation();
  const wrapRef = useRef(null);
  const [menu, setMenu] = useState(null); // id colonna oppure 'new'
  const [rowMenu, setRowMenu] = useState(null); // {task, top, left}
  const [resize, setResize] = useState(null); // {id, width}
  const [dragId, setDragId] = useState(null); // colonna in trascinamento
  const [dragTask, setDragTask] = useState(null); // riga in trascinamento
  const [dropMark, setDropMark] = useState(null); // {id, where: 'before'|'after'}
  const [collapsed, setCollapsed] = useState({}); // sezioni dei gruppi e attività compresse

  const visible = orderedColumns(columns, config);
  const widthOf = (column) => (resize && resize.id === column.id ? resize.width : columnWidth(config, column));
  const totalWidth = visible.reduce((sum, c) => sum + widthOf(c), 0) + END_COLUMNS_WIDTH + LEAD_WIDTH;

  const rowsOf = (list) => (treeMode ? flattenTree(list, collapsed) : list.map((task) => ({ task, depth: 0, childCount: 0, collapsed: false })));
  const allVisibleIds = (groups ? groups.flatMap((g) => g.tasks) : tasks).map((task) => task.id);
  const allSelected = allVisibleIds.length > 0 && allVisibleIds.every((id) => selectedIds.has(id));

  const startResize = (e, column) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startWidth = columnWidth(config, column);
    let width = startWidth;
    const onMove = (ev) => {
      width = Math.max(MIN_WIDTH, startWidth + ev.clientX - startX);
      setResize({ id: column.id, width });
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      setResize(null);
      if (width !== startWidth) onConfig({ widths: { ...(config.widths || {}), [column.id]: width } });
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const dropColumn = (targetId) => {
    if (dragId === null || dragId === targetId) return;
    const ids = allColumnsInOrder(columns, config).filter((c) => c.type !== 'title').map((c) => c.id).filter((id) => id !== dragId);
    ids.splice(ids.indexOf(targetId), 0, dragId);
    onConfig({ order: ids });
    setDragId(null);
  };

  // --- Tastiera ---

  const focusCell = (tr, cellIndex) => {
    const target = tr?.children[cellIndex]?.querySelector('input:not([type=hidden]):not([type=file]):not([disabled]), select, textarea, button.dd-btn');
    if (target) {
      target.focus();
      if (target.select && target.type !== 'date' && target.type !== 'checkbox') target.select();
      return true;
    }
    return false;
  };

  const onTableKeyDown = (e) => {
    const el = e.target;
    const td = el.closest('td.pd-cell');
    if (!td || !['INPUT', 'SELECT'].includes(el.tagName) || el.closest('.pd-popover')) return;
    const tr = td.parentElement;
    const index = [...tr.children].indexOf(td);
    const textLike = el.tagName === 'INPUT' && ['text', 'email', 'tel', 'url', ''].includes(el.type);
    const vertical = (e.key === 'ArrowUp' || e.key === 'ArrowDown') && (e.altKey || textLike);
    const enterDown = e.key === 'Enter' && textLike && !e.shiftKey;
    if (!vertical && !enterDown) {
      if (e.key === 'Escape' && el.blur) el.blur();
      return;
    }
    const dir = e.key === 'ArrowUp' ? -1 : 1;
    let next = dir === -1 ? tr.previousElementSibling : tr.nextElementSibling;
    while (next && next.classList.contains('pd-group-row')) next = dir === -1 ? next.previousElementSibling : next.nextElementSibling;
    if (!next) return;
    e.preventDefault();
    el.blur(); // salva la cella lasciata
    setTimeout(() => focusCell(next, index), 0);
  };

  // --- Incolla da Excel ---

  const onTablePaste = (e) => {
    const el = e.target;
    const td = el.closest && el.closest('td.pd-cell');
    const text = e.clipboardData ? e.clipboardData.getData('text/plain') : '';
    if (!td || !text || !/[\t\n]/.test(text.replace(/\r?\n$/, ''))) return; // una sola cella: incolla normale
    const tr = td.parentElement;
    const columnIndex = [...tr.children].indexOf(td) - 1; // la prima cella è la selezione
    if (columnIndex < 0 || columnIndex >= visible.length) return;
    e.preventDefault();
    onPasteTable(Number(tr.dataset.taskId), columnIndex, parseClipboardTable(text.replace(/\r?\n$/, '')));
  };

  // --- Trascinamento righe ---

  const markFor = (e, taskId) => {
    const rect = e.currentTarget.getBoundingClientRect();
    return { id: taskId, where: e.clientY < rect.top + rect.height / 2 ? 'before' : 'after' };
  };

  const rowDragOver = (e, task) => {
    if (!dragTask || dragTask.id === task.id || descendantIds(allTasks, dragTask.id).has(task.id)) return;
    e.preventDefault();
    setDropMark(markFor(e, task.id));
  };

  const rowDrop = (e, task) => {
    e.preventDefault();
    const mark = markFor(e, task.id);
    const source = dragTask;
    setDragTask(null);
    setDropMark(null);
    if (source && source.id !== task.id) onReorder(source.id, task.id, mark.where);
  };

  const openRowMenu = (e, task) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setRowMenu({ task, top: rect.bottom + 2, left: Math.max(8, Math.min(rect.left - 150, window.innerWidth - 200)) });
  };

  const renderRow = ({ task, depth, childCount, collapsed: isCollapsed }) => (
    <tr
      key={task.id}
      data-task-id={task.id}
      className={(selectedIds.has(task.id) ? 'pd-row-selected ' : '') + (dropMark && dropMark.id === task.id ? `pd-drop-${dropMark.where}` : '')}
      onDragOver={(e) => rowDragOver(e, task)}
      onDragLeave={() => dropMark && dropMark.id === task.id && setDropMark(null)}
      onDrop={(e) => rowDrop(e, task)}
    >
      <td className="pd-lead">
        <input
          type="checkbox"
          className="pd-select-box"
          checked={selectedIds.has(task.id)}
          aria-label={t('projectDetail.selectRow')}
          onChange={() => {}}
          onClick={(e) => onToggleSelect(task.id, e.shiftKey)}
        />
        {canReorder && (
          <span
            className="pd-grip"
            draggable
            role="button"
            aria-label={t('projectDetail.dragRow')}
            title={t('projectDetail.dragRow')}
            onDragStart={(e) => {
              e.dataTransfer.setData('text/plain', String(task.id));
              e.dataTransfer.effectAllowed = 'move';
              setDragTask(task);
            }}
            onDragEnd={() => { setDragTask(null); setDropMark(null); }}
          >
            <NtIcon name="grip" />
          </span>
        )}
      </td>
      {visible.map((column) => (
        <td key={column.id} className={'pd-cell' + (column.type === 'title' ? ' pd-sticky' : '')}>
          {column.type === 'title' ? (
            <div className="pd-title-cell" style={{ paddingLeft: depth * 18 }}>
              {treeMode && (childCount > 0 ? (
                <button type="button" className="pd-tree-toggle" aria-expanded={!isCollapsed} aria-label={t('projectDetail.toggleSubtasks')} onClick={() => setCollapsed({ ...collapsed, [task.id]: !isCollapsed })}>
                  <span className={'pd-chevron' + (isCollapsed ? '' : ' pd-chevron-open')} aria-hidden="true">▸</span>
                </button>
              ) : <span className="pd-tree-spacer" />)}
              <input
                key={`${task.id}:${task.title}`}
                className="pd-input pd-input-title"
                defaultValue={task.title}
                placeholder={t('projectDetail.untitled')}
                autoFocus={task.id === focusTaskId}
                aria-label={column.name}
                onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
                onBlur={(e) => {
                  if (task.id === focusTaskId) onFocusDone();
                  if (e.target.value.trim() !== task.title) onPatchTask(task.id, { title: e.target.value.trim() });
                }}
              />
              {childCount > 0 && <span className="nt-muted pd-child-count" title={t('projectDetail.subtasks')}>{childCount}</span>}
              <button type="button" className="pd-open-btn" onClick={() => onOpenTask(task)}>{t('projectDetail.open')}</button>
            </div>
          ) : (
            <ProjectCell
              task={task}
              column={column}
              users={users}
              allTasks={allTasks}
              onSave={(value) => onPatchTask(task.id, { data: { [String(column.id)]: value } })}
              onTaskReplace={onTaskReplace}
              onError={onError}
            />
          )}
        </td>
      ))}
      <td className="pd-cell">
        <div className="pd-ticket-chips">
          {task.tickets.map((tk) => (
            <Link key={tk.id} to={`/tickets/${tk.id}`} state={{ from: `${location.pathname}${location.search}` }} className="pd-ticket-chip" title={tk.subject}>#{tk.id}</Link>
          ))}
          <button type="button" className="pd-row-delete pd-ticket-open" style={{ opacity: 1 }} onClick={() => onOpenTask(task)} aria-label={t('projectDetail.linkedTickets')} title={t('projectDetail.linkedTickets')}>+</button>
        </div>
      </td>
      <td className="pd-cell-end">
        <button type="button" className="pd-row-delete" onClick={(e) => openRowMenu(e, task)} aria-label={t('projectDetail.rowActions')} title={t('projectDetail.rowActions')}>
          <NtIcon name="more" />
        </button>
      </td>
    </tr>
  );

  const colSpan = visible.length + 3;

  return (
    <div className="nt-table-wrap pd-table-wrap" ref={wrapRef} onKeyDown={onTableKeyDown} onPaste={onTablePaste}>
      <table className="nt-table pd-table" style={{ width: totalWidth, minWidth: '100%' }}>
        <colgroup>
          <col style={{ width: LEAD_WIDTH }} />
          {visible.map((column) => <col key={column.id} style={{ width: widthOf(column) }} />)}
          <col style={{ width: 150 }} />
          <col style={{ width: 40 }} />
        </colgroup>
        <thead>
          <tr>
            <th className="pd-th pd-lead">
              <input
                type="checkbox"
                className="pd-select-box"
                checked={allSelected}
                aria-label={t('projectDetail.selectAll')}
                onChange={(e) => onToggleAll(allVisibleIds, e.target.checked)}
              />
            </th>
            {visible.map((column) => (
              <th
                key={column.id}
                className={'pd-th' + (column.type === 'title' ? ' pd-sticky' : '')}
                draggable={column.type !== 'title'}
                onDragStart={() => setDragId(column.id)}
                onDragOver={(e) => column.type !== 'title' && dragId !== null && e.preventDefault()}
                onDrop={() => dropColumn(column.id)}
              >
                <button type="button" className="pd-th-btn" onClick={() => setMenu(column.id)}>
                  <span className="nt-th">
                    <NtIcon name={TYPE_ICONS[column.type]} />
                    <span className="pd-th-name">{column.name}</span>
                  </span>
                </button>
                <span className="pd-resize" onMouseDown={(e) => startResize(e, column)} role="separator" aria-label={t('projectView.resizeColumn')} />
                {menu === column.id && (
                  <ColumnMenuHost
                    column={column}
                    onClose={() => setMenu(null)}
                    onColumnSubmit={onColumnSubmit}
                    onColumnAction={onColumnAction}
                  />
                )}
              </th>
            ))}
            <th className="pd-th">
              <span className="pd-th-btn nt-th"><NtIcon name="ticket" />{t('projectDetail.ticketsColumn')}</span>
            </th>
            <th className="pd-th pd-th-add">
              <button type="button" className="pd-th-btn" onClick={() => setMenu('new')} aria-label={t('projectDetail.addColumn')}>+</button>
              {menu === 'new' && (
                <ColumnMenu
                  column={null}
                  onClose={() => setMenu(null)}
                  onSubmit={(payload) => { setMenu(null); onColumnSubmit(null, payload); }}
                />
              )}
            </th>
          </tr>
        </thead>
        <tbody>
          {groups ? groups.map((group) => (
            <React.Fragment key={group.key || 'none'}>
              <tr className="pd-group-row">
                <td colSpan={colSpan}>
                  <button type="button" className="pd-group-toggle" onClick={() => setCollapsed({ ...collapsed, [`g:${group.key}`]: !collapsed[`g:${group.key}`] })} aria-expanded={!collapsed[`g:${group.key}`]}>
                    <span className={'pd-chevron' + (collapsed[`g:${group.key}`] ? '' : ' pd-chevron-open')} aria-hidden="true">▸</span>
                    {group.option ? <Pill dot color={optionColor(group.option)}>{group.label}</Pill> : <span className="nt-muted">{group.label || t('projectView.noValue')}</span>}
                    <span className="nt-muted pd-group-count">{group.tasks.length}</span>
                  </button>
                  <button type="button" className="pd-row-delete pd-group-add" style={{ opacity: 1 }} onClick={() => onAddTask(groupKeyToValue(groupColumn, group.key))} aria-label={t('projectDetail.newTask')}>+</button>
                </td>
              </tr>
              {!collapsed[`g:${group.key}`] && rowsOf(group.tasks).map(renderRow)}
            </React.Fragment>
          )) : rowsOf(tasks).map(renderRow)}
        </tbody>
      </table>
      {tasks.length === 0 && <p className="nt-empty">{t('projectDetail.noTasks')}</p>}
      <button type="button" className="nt-add-row" onClick={() => onAddTask(null)}>
        <span aria-hidden="true">+</span> {t('projectDetail.newTask')}
      </button>

      {rowMenu && (
        <>
          <div className="pd-overlay" onClick={() => setRowMenu(null)} />
          <div className="pd-popover pv-menu" style={{ position: 'fixed', top: rowMenu.top, left: rowMenu.left }}>
            {[
              ['open', 'edit', t('projectDetail.open')],
              ['subtask', 'plus', t('projectDetail.addSubtask')],
              ['duplicate', 'copy', t('projectDetail.duplicateTask')],
              ['template', 'layout', t('projectDetail.saveAsTemplate')],
              ['delete', 'trash', t('projectDetail.deleteTask')],
            ].map(([action, icon, label]) => (
              <button key={action} type="button" className="pv-menu-item" onClick={() => { const task = rowMenu.task; setRowMenu(null); onRowAction(action, task); }}>
                <NtIcon name={icon} /> {label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default ProjectTable;
